import { useState } from "react";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  REITHoldingFormData,
  REITType,
  REITSector,
} from "@/hooks/reits/useREITHoldings";
import { useDividendFrequencies } from "@/hooks/useDividendFrequencies";
import { useCurrencyList } from "@/hooks/useCurrencyList";
import useFetchReitTypes from "../../hooks/reits/useFetchReitTypes.ts";
import {ReitHolding} from "@/@types/reit.models.ts";
import useFetchReitSectors from "../../hooks/reits/useFetchReitSectors.ts";

interface REITFormProps {
  holding?: ReitHolding | null;
  onSave: (data: REITHoldingFormData) => void | Promise<void>;
  onCancel: () => void;
}

export default function REITForm({ holding, onSave, onCancel }: REITFormProps) {
  const {
    data: reitTypes,
    isLoading: typesLoading,
    error: typesError,
  } = useFetchReitTypes();
  const {
    data: reitSectors,
    isLoading: sectorsLoading,
    error: sectorsError,
  } = useFetchReitSectors();
  const { data: dividendFrequencies, isLoading: freqLoading } =
    useDividendFrequencies();
  const isLoading = typesLoading || sectorsLoading;
  const metadataError = typesError || sectorsError;

  const [reitName, setReitName] = useState(holding?.reit_name ?? "");
  const [tickerSymbol, setTickerSymbol] = useState(
    holding?.ticker_symbol ?? "",
  );
  const [reitType, setReitType] = useState<REITType>(
    holding?.reit_type ?? "equity",
  );
  const [sector, setSector] = useState<REITSector>(
    holding?.sector ?? "diversified",
  );
  const [unitsHeld, setUnitsHeld] = useState(
    holding?.units_held?.toString() ?? "0",
  );
  const [purchasePrice, setPurchasePrice] = useState(
    holding?.purchase_price_per_unit?.toString() ?? "0",
  );
  const [currentPrice, setCurrentPrice] = useState(
    holding?.current_price_per_unit?.toString() ?? "0",
  );
  const [currency, setCurrency] = useState(holding?.currency ?? "USD");
  const [purchaseDate, setPurchaseDate] = useState(
    holding?.purchase_date ?? new Date().toISOString().split("T")[0],
  );
  const [dividendPerUnit, setDividendPerUnit] = useState(
    holding?.dividend_per_unit?.toString() ?? "0",
  );
  const [dividendFrequency, setDividendFrequency] = useState<string>(
    holding?.dividend_frequency ?? "quarterly",
  );
  const [navPerUnit, setNavPerUnit] = useState(
    holding?.nav_per_unit?.toString() ?? "",
  );
  const [occupancyRate, setOccupancyRate] = useState(
    holding?.occupancy_rate?.toString() ?? "",
  );
  const [notes, setNotes] = useState(holding?.notes ?? "");
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reitName.trim()) return;
    onSave({
      reit_name: reitName.trim(),
      ticker_symbol: tickerSymbol.trim().toUpperCase() || undefined,
      reit_type: reitType,
      sector,
      units_held: parseFloat(unitsHeld) || 0,
      purchase_price_per_unit: parseFloat(purchasePrice) || 0,
      current_price_per_unit: parseFloat(currentPrice) || 0,
      currency,
      purchase_date: purchaseDate,
      dividend_per_unit: parseFloat(dividendPerUnit) || 0,
      dividend_frequency: dividendFrequency,
      nav_per_unit: navPerUnit ? parseFloat(navPerUnit) : undefined,
      occupancy_rate: occupancyRate ? parseFloat(occupancyRate) : undefined,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4"
      aria-label={holding ? "Edit REIT holding" : "Add REIT holding"}>
      {metadataError && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            Could not load REIT metadata. Type and sector options are
            unavailable.
          </AlertDescription>
        </Alert>
      )}

      {/* Name & Ticker */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="reit-name">REIT Name *</Label>
          <Input
            id="reit-name"
            value={reitName}
            onChange={(e) => setReitName(e.target.value)}
            placeholder="e.g. Vanguard Real Estate ETF"
            required
            aria-required="true"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="reit-ticker">Ticker Symbol</Label>
          <Input
            id="reit-ticker"
            value={tickerSymbol}
            onChange={(e) => setTickerSymbol(e.target.value)}
            placeholder="e.g. VNQ"
            aria-label="Ticker symbol (optional)"
          />
        </div>
      </div>

      {/* Type & Sector */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="reit-type">REIT Type</Label>
          <Select
            value={reitType}
            onValueChange={(v) => setReitType(v as REITType)}>
            <SelectTrigger
              id="reit-type"
              aria-label="Select REIT type"
              disabled={isLoading}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(reitTypes ?? []).map((t) => (
                <SelectItem key={t.name} value={t.name}>
                  <div className="flex items-center gap-2 capitalize">
                    {t.name}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">{t.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="reit-sector">Sector</Label>
          <Select
            value={sector}
            onValueChange={(v) => setSector(v as REITSector)}>
            <SelectTrigger
              id="reit-sector"
              aria-label="Select sector"
              disabled={isLoading}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(reitSectors ?? []).map((s) => (
                <SelectItem key={s.name} value={s.name}>
                  <div className="flex items-center gap-2 capitalize">
                    {s.name}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">{s.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Units & Currency */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="reit-units">Units / Shares Held</Label>
          <Input
            id="reit-units"
            type="number"
            step="0.001"
            min="0"
            value={unitsHeld}
            onChange={(e) => setUnitsHeld(e.target.value)}
            aria-label="Number of units or shares held"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="reit-currency">Currency</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger id="reit-currency" aria-label="Select currency">
              <SelectValue />
            </SelectTrigger>
            {isLoadingCurrencies && "Loading Currencies"}
            {!isLoadingCurrencies && !errorLoadingCurrencies ? (
              <SelectContent>
                {availableCurrencies.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            ) : (
              "Failed to load currencies"
            )}
          </Select>
        </div>
      </div>

      {/* Prices */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="reit-purchase-price">Purchase Price (per unit)</Label>
          <Input
            id="reit-purchase-price"
            type="number"
            step="0.0001"
            min="0"
            value={purchasePrice}
            onChange={(e) => setPurchasePrice(e.target.value)}
            aria-label="Purchase price per unit"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="reit-current-price">Current Price (per unit)</Label>
          <Input
            id="reit-current-price"
            type="number"
            step="0.0001"
            min="0"
            value={currentPrice}
            onChange={(e) => setCurrentPrice(e.target.value)}
            aria-label="Current price per unit"
          />
        </div>
      </div>

      {/* Purchase Date */}
      <div className="space-y-2">
        <Label htmlFor="reit-purchase-date">Purchase Date</Label>
        <Input
          id="reit-purchase-date"
          type="date"
          value={purchaseDate}
          onChange={(e) => setPurchaseDate(e.target.value)}
          aria-label="Purchase date"
        />
      </div>

      {/* Dividend */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="reit-dividend-per-unit">
            Annual Dividend (per unit)
          </Label>
          <Input
            id="reit-dividend-per-unit"
            type="number"
            step="0.0001"
            min="0"
            value={dividendPerUnit}
            onChange={(e) => setDividendPerUnit(e.target.value)}
            aria-label="Annual dividend per unit"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="reit-dividend-frequency">Dividend Frequency</Label>
          <Select
            value={dividendFrequency}
            onValueChange={(v) => setDividendFrequency(v)}>
            <SelectTrigger
              id="reit-dividend-frequency"
              aria-label="Select dividend frequency"
              disabled={freqLoading}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(dividendFrequencies ?? []).map((f) => (
                <SelectItem key={f.name} value={f.name}>
                  <div className="flex items-center gap-2 capitalize">
                    {f.name}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">{f.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Optional: NAV & Occupancy */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="reit-nav">NAV per Unit (optional)</Label>
          <Input
            id="reit-nav"
            type="number"
            step="0.0001"
            min="0"
            value={navPerUnit}
            onChange={(e) => setNavPerUnit(e.target.value)}
            placeholder="Net Asset Value per unit"
            aria-label="Net asset value per unit (optional)"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="reit-occupancy">Occupancy Rate % (optional)</Label>
          <Input
            id="reit-occupancy"
            type="number"
            step="0.1"
            min="0"
            max="100"
            value={occupancyRate}
            onChange={(e) => setOccupancyRate(e.target.value)}
            placeholder="e.g. 95.5"
            aria-label="Occupancy rate percentage (optional)"
          />
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="reit-notes">Notes</Label>
        <Textarea
          id="reit-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes about this holding"
          rows={2}
          aria-label="Optional notes"
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {holding ? "Update Holding" : "Add Holding"}
        </Button>
      </DialogFooter>
    </form>
  );
}
